import { useCallback } from 'react';
import { useToastContext } from '@infotrack/zenith-ui';
import { useQueryClient } from '@tanstack/react-query';
import {
  useITopFiveApiV2EndpointsTopFiveUpdateTopFiveUpdateTopFiveEndpoint,
  getITopFiveApiV2EndpointsTopFiveGetTopFiveByIdGetTopFiveByIdEndpointQueryKey,
} from '@/api/api.generated';
import type { ITopFiveApiV2EndpointsTopFiveUpdateTopFiveUpdateTopFiveItemDto } from '@/api/models';

type UseUpdateTopFiveParams = {
  topFiveId: number;
  onSuccess?: () => void;
};

export const useUpdateTopFive = ({
  topFiveId,
  onSuccess,
}: UseUpdateTopFiveParams) => {
  const mutation =
    useITopFiveApiV2EndpointsTopFiveUpdateTopFiveUpdateTopFiveEndpoint();
  const toastContext = useToastContext();
  const queryClient = useQueryClient();

  const updateTopFiveItems = useCallback(
    async (
      items: ITopFiveApiV2EndpointsTopFiveUpdateTopFiveUpdateTopFiveItemDto[]
    ) => {
      try {
        const response = await mutation.mutateAsync({
          topFiveId,
          data: { items },
        });

        toastContext.success({
          title: 'TOP 5 Updated',
          message: `${response.data?.itemsUpdated || items.length} item(s) updated successfully`,
        });

        // Invalidate and refetch the TopFive detail query
        await queryClient.invalidateQueries({
          queryKey:
            getITopFiveApiV2EndpointsTopFiveGetTopFiveByIdGetTopFiveByIdEndpointQueryKey(
              topFiveId
            ),
        });

        onSuccess?.();
        return response;
      } catch (error) {
        toastContext.error({
          title: 'Update Failed',
          message: 'Failed to update TOP 5 items. Please try again.',
        });
        throw error;
      }
    },
    [mutation, topFiveId, toastContext, queryClient, onSuccess]
  );

  return {
    updateTopFiveItems,
    isLoading: mutation.isPending,
    error: mutation.error,
  };
};
